const INCOMING_ATTACHED_TO = 4;
const CHAT_VOICE_ATTACHED_TO = 5;
const OFFICE_ONLY_ATTACHED_TO = 6;

function isIncomingVoicemail(memo = {}) {
  return Number(memo.attachedTo) === INCOMING_ATTACHED_TO;
}

function incomingVoiceMessages(list) {
  return (Array.isArray(list) ? list : []).filter(isIncomingVoicemail);
}

function isVoiceInboxMemo(memo = {}) {
  if (memo == null) return false;
  if (memo.attachedTo == null || memo.attachedTo === '') return true;
  const n = Number(memo.attachedTo);
  if (!Number.isFinite(n) || n === 0) return true;
  return (
    n === INCOMING_ATTACHED_TO ||
    n === CHAT_VOICE_ATTACHED_TO ||
    n === OFFICE_ONLY_ATTACHED_TO
  );
}

function isOfficeOnlyVoiceMemo(memo = {}) {
  return Number(memo && memo.attachedTo) === OFFICE_ONLY_ATTACHED_TO;
}

function canSeeOfficeVoiceMemos(role) {
  const n = Number(role);
  return Number.isFinite(n) && n < 3;
}

function omitOfficeOnlyVoiceMemos(list, role) {
  const rows = Array.isArray(list) ? list : [];
  if (canSeeOfficeVoiceMemos(role)) return rows;
  return rows.filter((row) => !isOfficeOnlyVoiceMemo(row));
}

function voiceMessagesForRole(list, role) {
  return omitOfficeOnlyVoiceMemos(inboxVoiceMessages(list), role);
}

function inboxVoiceMessages(list) {
  return (Array.isArray(list) ? list : []).filter(isVoiceInboxMemo);
}

function voiceMessageSource(memo = {}) {
  const n = Number(memo.attachedTo);
  if (n === INCOMING_ATTACHED_TO) return 'voicemail';
  if (n === CHAT_VOICE_ATTACHED_TO) return 'chat';
  if (n === OFFICE_ONLY_ATTACHED_TO) return 'office';
  return 'staff';
}

function voiceMessageSourceLabel(source) {
  if (source === 'chat') return 'Chat';
  if (source === 'staff') return 'Staff';
  if (source === 'office') return 'office';
  return 'Voicemail';
}

const VOICEMAIL_FROM_PREFIX = /^(?:voicemail|voice mail) from\s+/i;

function voiceMessageDisplayTitle(memo = {}) {
  const title = String((memo && memo.title) || '');
  if (voiceMessageSource(memo) !== 'voicemail') return title;
  return title.replace(VOICEMAIL_FROM_PREFIX, '');
}

function filterVoiceMessagesSearch(list, query) {
  const q = String(query || '')
    .trim()
    .toLowerCase();
  const rows = Array.isArray(list) ? list : [];
  if (!q) return rows.slice();
  return rows.filter((row) => {
    const hay = [row.title, row.note, row.createdBy]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return hay.includes(q);
  });
}

function voiceMessageHasTranscript(memo = {}) {
  return String(memo.note || '').trim().length > 0;
}

function voiceMessageTranscriptText(memo = {}) {
  const text = String(memo.note || '').trim();
  return text || 'No transcript yet.';
}

function voiceMessagesIsEmbedded(routePath) {
  return String(routePath || '') !== '/mvoice';
}

function isIncomingUnlistened(memo = {}) {
  if (!isVoiceInboxMemo(memo)) return false;
  if (Number(memo.deleted) === 1) return false;
  return memo.listenedOn == null || memo.listenedOn === '';
}

function voiceMessagesBadgeCount(list) {
  return (Array.isArray(list) ? list : []).filter(isIncomingUnlistened).length;
}

const {
  DATE_GROUP_DEFS: VOICE_MESSAGE_GROUP_DEFS,
  dateBucket: voiceMessageDateBucket,
  dateGroupCollapsedByDefault: voiceMessageGroupCollapsedByDefault,
  groupByDate,
} = require('./dateGroups');

function groupVoiceMessagesByDate(list, now = new Date()) {
  return groupByDate(list, now, (row) => row && row.createdOn);
}

function formatVoicePlaybackClock(seconds) {
  const n = Number(seconds);
  if (!Number.isFinite(n) || n < 0) return '0:00';
  const total = Math.floor(n);
  const m = Math.floor(total / 60);
  const s = String(total % 60).padStart(2, '0');
  return `${m}:${s}`;
}

function clampVoiceSeekTime(time, duration) {
  const dur = Number(duration);
  const t = Number(time);
  if (!Number.isFinite(dur) || dur <= 0) return 0;
  if (!Number.isFinite(t) || t < 0) return 0;
  return t > dur ? dur : t;
}

function readAudioDuration(audio) {
  if (!audio) return 0;
  const dur = Number(audio.duration);
  if (Number.isFinite(dur) && dur > 0) return dur;
  try {
    const seekable = audio.seekable;
    if (seekable && seekable.length) {
      const end = Number(seekable.end(seekable.length - 1));
      if (Number.isFinite(end) && end > 0 && end < 1e6) return end;
    }
  } catch (err) {
    /* A media element with no source can throw when seekable is read. */
  }
  return 0;
}

function revealAudioDuration(audio, timeoutMs = 1500) {
  const known = readAudioDuration(audio);
  if (known || !audio) return Promise.resolve(known);
  return new Promise((resolve) => {
    let settled = false;
    let rewindTimer = null;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      audio.removeEventListener('durationchange', onDuration);
      audio.removeEventListener('loadedmetadata', onReady);
      audio.removeEventListener('seeked', onRewound);
      clearTimeout(timer);
      clearTimeout(rewindTimer);
      resolve(value || 0);
    };
    const onRewound = () => finish(readAudioDuration(audio));
    const rewind = (value) => {
      if (!(Number(audio.currentTime) > 1)) {
        finish(value);
        return;
      }
      audio.addEventListener('seeked', onRewound);
      rewindTimer = setTimeout(() => finish(value), 300);
      try {
        audio.currentTime = 0;
      } catch (err) {
        finish(value);
      }
    };
    const onDuration = () => {
      const dur = readAudioDuration(audio);
      if (!dur) return;
      rewind(dur);
    };
    const kick = () => {
      if (readAudioDuration(audio)) {
        onDuration();
        return;
      }
      try {
        audio.currentTime = 1e101;
      } catch (err) {
        finish(0);
      }
    };
    const onReady = () => kick();
    audio.addEventListener('durationchange', onDuration);
    audio.addEventListener('loadedmetadata', onReady);
    const timer = setTimeout(() => rewind(readAudioDuration(audio)), timeoutMs);
    if (audio.readyState >= 1) kick();
  });
}

function measureBlobDuration(blob, decodeAudioData) {
  if (!blob || typeof decodeAudioData !== 'function') return Promise.resolve(0);
  return blob.arrayBuffer().then(
    (bytes) =>
      decodeAudioData(bytes.slice(0))
        .then((decoded) => {
          const dur = Number(decoded && decoded.duration);
          if (Number.isFinite(dur) && dur > 0 && dur < 1e6) return dur;
          return 0;
        })
        .catch(() => 0),
    () => 0,
  );
}

function applyScrubBar(bar, current, duration, previousMax) {
  const seen = Number(previousMax) || 0;
  if (!bar) return seen;
  const dur = Number(duration);
  const cur = Number(current);
  const max = Number.isFinite(dur) && dur > 0 && dur < 1e6 ? dur : 0;
  const value = Number.isFinite(cur) && cur > 0 ? cur : 0;
  const clamped = max > 0 && value > max ? max : value;
  if (!(max > 0)) {
    bar.value = '0';
    return seen;
  }
  bar.max = String(max);
  if (seen !== max) {
    // A range created at max 0 keeps its thumb until value changes after max.
    bar.value = clamped === 0 ? String(max) : '0';
  }
  bar.value = String(clamped);
  return max;
}

function sniffAudioMimeFromBytes(bytes) {
  const b = bytes || [];
  const at = (i) => Number(b[i]);
  if (at(0) === 0x52 && at(1) === 0x49 && at(2) === 0x46 && at(3) === 0x46) {
    return 'audio/wav';
  }
  if (at(0) === 0x4f && at(1) === 0x67 && at(2) === 0x67 && at(3) === 0x53) {
    return 'audio/ogg';
  }
  if (at(0) === 0x1a && at(1) === 0x45) return 'audio/webm';
  if (
    at(4) === 0x66 &&
    at(5) === 0x74 &&
    at(6) === 0x79 &&
    at(7) === 0x70
  ) {
    return 'audio/mp4';
  }
  if (at(0) === 0x49 && at(1) === 0x44 && at(2) === 0x33) return 'audio/mpeg';
  return 'audio/wav';
}

function voicePlaybackParams(userAgent) {
  if (/iPhone|iPad|iPod/i.test(String(userAgent || ''))) return { ios: '1' };
  return {};
}

const UNKNOWN_VOICEMAIL_TITLE = /^Voicemail from (\d{3}-\d{3}-\d{4}) · unknown$/;
const NAMED_EXTERNAL_VOICEMAIL_TITLE =
  /^Voicemail from (\d{3}-\d{3}-\d{4}) · (.+) \(external contact\)$/;

function unknownVoicemailCaller(title) {
  const match = String(title || '').match(UNKNOWN_VOICEMAIL_TITLE);
  if (!match) return null;
  return { phone: match[1], digits: match[1].replace(/-/g, '') };
}

function externalVoicemailCaller(title) {
  const match = String(title || '').match(NAMED_EXTERNAL_VOICEMAIL_TITLE);
  if (!match) return null;
  const name = String(match[2] || '').trim();
  if (!name) return null;
  return { phone: match[1], digits: match[1].replace(/-/g, ''), name };
}

function externalVoicemailTitle(digits, name) {
  const raw = String(digits || '').replace(/\D/g, '');
  const ten = raw.length === 11 && raw.startsWith('1') ? raw.slice(1) : raw;
  const phone =
    ten.length === 10
      ? `${ten.slice(0, 3)}-${ten.slice(3, 6)}-${ten.slice(6)}`
      : ten;
  const prefix = `Voicemail from ${phone} · `;
  const suffix = ' (external contact)';
  const room = 100 - prefix.length - suffix.length;
  const label = String(name || '').trim().slice(0, Math.max(1, room));
  return `${prefix}${label}${suffix}`;
}

function voiceTitleWithExternalName(title, name) {
  const caller = unknownVoicemailCaller(title);
  const label = String(name || '').trim();
  if (!caller || !label) return title;
  return `Voicemail from ${caller.phone} · ${label} (external contact)`;
}

function applyExternalNameToVoiceInbox(list, digits, name) {
  const wanted = String(digits || '').replace(/\D/g, '');
  const label = String(name || '').trim();
  return (Array.isArray(list) ? list : []).map((row) => {
    const caller = unknownVoicemailCaller(row && row.title);
    if (!caller || caller.digits !== wanted || !label) return row;
    return { ...row, title: voiceTitleWithExternalName(row.title, label) };
  });
}

function applyExternalEditToVoiceInbox(list, fromDigits, digits, name) {
  const fromRaw = String(fromDigits || '').replace(/\D/g, '');
  const toRaw = String(digits || '').replace(/\D/g, '');
  const from = fromRaw.length === 11 && fromRaw.startsWith('1') ? fromRaw.slice(1) : fromRaw;
  const to = toRaw.length === 11 && toRaw.startsWith('1') ? toRaw.slice(1) : toRaw;
  const label = String(name || '').trim();
  if (from.length !== 10 || to.length !== 10 || !label) {
    return Array.isArray(list) ? list.slice() : [];
  }
  return (Array.isArray(list) ? list : []).map((row) => {
    const caller =
      unknownVoicemailCaller(row && row.title) ||
      externalVoicemailCaller(row && row.title);
    if (!caller || caller.digits !== from) return row;
    return { ...row, title: externalVoicemailTitle(to, label) };
  });
}

function dropVoiceMessageById(list, id) {
  const drop = Number(id);
  return (Array.isArray(list) ? list : []).filter(
    (row) => Number(row && row.id) !== drop,
  );
}

module.exports = {
  INCOMING_ATTACHED_TO,
  CHAT_VOICE_ATTACHED_TO,
  OFFICE_ONLY_ATTACHED_TO,
  isIncomingVoicemail,
  incomingVoiceMessages,
  isVoiceInboxMemo,
  isOfficeOnlyVoiceMemo,
  canSeeOfficeVoiceMemos,
  omitOfficeOnlyVoiceMemos,
  voiceMessagesForRole,
  inboxVoiceMessages,
  voiceMessageSource,
  voiceMessageSourceLabel,
  voiceMessageDisplayTitle,
  filterVoiceMessagesSearch,
  voiceMessageHasTranscript,
  voiceMessageTranscriptText,
  voiceMessagesIsEmbedded,
  isIncomingUnlistened,
  voiceMessagesBadgeCount,
  VOICE_MESSAGE_GROUP_DEFS,
  voiceMessageDateBucket,
  voiceMessageGroupCollapsedByDefault,
  groupVoiceMessagesByDate,
  formatVoicePlaybackClock,
  clampVoiceSeekTime,
  readAudioDuration,
  revealAudioDuration,
  measureBlobDuration,
  applyScrubBar,
  unknownVoicemailCaller,
  externalVoicemailCaller,
  externalVoicemailTitle,
  voiceTitleWithExternalName,
  applyExternalNameToVoiceInbox,
  applyExternalEditToVoiceInbox,
  dropVoiceMessageById,
  sniffAudioMimeFromBytes,
  voicePlaybackParams,
};
